import { Alert, Grid2, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import {
	UNIVERSAL_ROLL_INITIAL_VALUES,
	UniversalRoll,
} from "@/pages/catalogs/models/UniversalRoll";
import { upsertUniversalRoll } from "@/pages/catalogs/repositories/UniversalRollRepository";
import {
	RollsetDeactivationConfirmModal,
	RollsetInfo,
} from "@/pages/catalogs/shared/components/RollsetDeactivationConfirmModal";
import { Stand } from "@/pages/configuration/models/Stand";
import {
	StandGrid,
	useStandsByPart,
} from "@/pages/configuration/pages/parts/StandGrid";
import { setStandsToPart } from "@/pages/configuration/repositories/StandRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { Section } from "@/shared/models/Section";
import { setSectionsToPart } from "@/shared/repositories/SectionRepository";
import { SectionField } from "@/shared/selectors/SectionField";
import { useSectionsByPart } from "@/shared/selectors/SectionsOfPartSelector";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	ArborSelector,
	DonutSelector,
	LocationSelector,
	LocationTreeSelector,
	RollPositionSelector,
	RollStatusSelector,
} from "../../../../../shared/selectors";
import { useArbors } from "../../arbors/ArborPage";
import { RollsetCard } from "@/pages/catalogs/shared/components/RollsetCard";

type Props = {
	roll: UniversalRoll | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const UniversalRollDetails = ({ roll, modal, onHide }: Props) => {
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const [sectionsSelected, setSectionsSelected] = useState<Section[]>([]);
	const [forceAssembly, setForceAssembly] = useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingFormData, setPendingFormData] = useState<UniversalRoll | null>(
		null,
	);

	const {
		control,
		reset,
		handleSubmit,
		getValues,
		setValue,
		formState: { dirtyFields },
	} = useForm<UniversalRoll>({
		defaultValues: UNIVERSAL_ROLL_INITIAL_VALUES,
		mode: "onChange",
	});

	const { data: arbors } = useArbors({
		partId: undefined,
		onError: (error) => setError(error.message),
	});

	const { data: sectionsOfPart, isSuccess: sectionsIsSuccess } =
		useSectionsByPart({
			partId: roll?.PartId ?? 0,
			onError: (error) => setError(error.message),
		});
	const sectionsJson = JSON.stringify(
		sectionsSelected.map((section: Section) => ({
			...section,
			PartId: roll?.PartId,
			PartName: roll?.PartName,
		})),
	);
	const oldSectionsJson = JSON.stringify(sectionsOfPart);

	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) ||
		sectionsJson !== oldSectionsJson;

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(
		({
			data,
			onForceAssembly,
		}: {
			data: UniversalRoll;
			onForceAssembly: boolean;
		}) => upsertUniversalRoll(data, onForceAssembly),
		{
			meta: { successMessage: `The roll was ${modal}d successfully` },
			onSuccess: async (id: number) => {
				if (id && sectionsSelected) {
					const resp = await setSectionsToPart(id, sectionsSelected);
					if (!resp.ok) setError(resp.message);
				}

				await queryClient.invalidateQueries({ queryKey: ["universalRolls"] });
				await queryClient.invalidateQueries({
					queryKey: ["sectionsByPart", id],
				});
				await queryClient.invalidateQueries({ queryKey: ["standsByPart", id] });
				onHide({ shouldUpdate: true, close: false, entityId: id });
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);

	const willDeactivateRollset = (formData: UniversalRoll): boolean => {
		if (!roll || modal === "create") return false;

		// Check if donut is being changed
		const donutChanged = formData.DonutPartId !== roll.DonutPartId;
		const statusChangedToBroken =
			formData.RollStatusCode === "K" && roll.RollStatusCode !== "K";

		if (!donutChanged && !statusChangedToBroken) return false;

		return roll.IsInActiveRollset && !roll.IsInActiveCarrier;
	};

	const executeSubmit = (data: UniversalRoll) => {
		setIsSubmitLoading(true);
		upsertMutation.mutate({ data, onForceAssembly: forceAssembly });
	};

	const onSubmit: SubmitHandler<UniversalRoll> = async (data) => {
		if (modal === "delete") return;

		if (willDeactivateRollset(data)) {
			setPendingFormData(data);
			setShowConfirmDialog(true);
			return;
		}

		executeSubmit(data);
	};

	const handleConfirmDeactivation = () => {
		setShowConfirmDialog(false);
		if (pendingFormData) {
			executeSubmit(pendingFormData);
			setPendingFormData(null);
		}
	};

	const handleCancelDeactivation = () => {
		setShowConfirmDialog(false);
		setPendingFormData(null);
	};

	useEffect(() => {
		if (sectionsOfPart !== undefined) {
			setSectionsSelected(sectionsOfPart || []);
		}
	}, [sectionsIsSuccess, sectionsOfPart]);

	useEffect(() => {
		if (modal !== "") {
			reset(roll || UNIVERSAL_ROLL_INITIAL_VALUES);
			setIsSubmitLoading(false);
			if (roll?.IsDuplicate) {
				if (sectionsOfPart && sectionsIsSuccess) {
					setSectionsSelected(sectionsOfPart);
				}
			} else {
				setSectionsSelected(sectionsOfPart || []);
			}
		}
		// Keyed on the part id, not the object: the table swaps in a fresh copy of the
		// selected row whenever its data refreshes (e.g. realtime updates from other
		// users), and resetting then wiped the edits in progress
	}, [modal, roll?.PartId]);

	const disabledStatusSelector = roll?.IsInActiveCarrier;

	// Rollset info for confirmation dialog
	const rollsetInfo: RollsetInfo | null = roll?.RollsetId
		? {
				RollsetId: roll.RollsetId,
				RollsetLetter: roll.RollsetLetter,
				RollsetSectionName: roll.RollsetSectionName,
				RollsetStandName: roll.RollsetStandName,
				RollsetTopRollName: roll.RollsetTopRollName,
				RollsetBottomRollName: roll.RollsetBottomRollName,
			}
		: null;

	return (
		<>
			{(modal === "create" || modal === "update") && (
				<HelmetDexteel
					title={`${
						roll === null ? "New Roll" : `${roll?.PartName ?? ""}`
					} - Universal Rolls`}
				/>
			)}
			{roll === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a roll or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						{!!roll?.IsInActiveCarrier && (
							<Grid2 size={{ xs: 12 }}>
								<Alert severity="warning">
									This roll is in Rollset {roll?.RollsetLetter} (Section:{" "}
									{roll?.RollsetSectionName}, Stand: {roll?.RollsetStandName})
									which is assigned to carrier{" "}
									<strong>{roll?.CarrierName}</strong> (Status:{" "}
									{roll?.CarrierStatusName}).
								</Alert>
							</Grid2>
						)}
						{!!roll?.IsInActiveRollset && !roll?.IsInActiveCarrier && (
							<Grid2 size={{ xs: 12 }}>
								<Alert severity="info">
									This roll is in an active Rollset {roll?.RollsetLetter}{" "}
									(Section: {roll?.RollsetSectionName}, Stand:{" "}
									{roll?.RollsetStandName}). Changing the Donut will deactivate
									the rollset.
								</Alert>
							</Grid2>
						)}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="ArborPartId"
								control={control}
								rules={{ required: "Arbor is required" }}
								render={({ field, fieldState: { error } }) => (
									<ArborSelector
										label="Arbor"
										value={field.value}
										onChange={(value: number | null, partName?: string) => {
											field.onChange(value);
											const arbor = arbors?.find(
												(arbor) => value === arbor.PartId,
											);
											if (arbor && arbor.StandsId != null) {
												const standsArray = arbor?.StandsId.split(",").map(
													(stand) => {
														const standId = parseInt(stand.trim());
														return {
															StandId: standId,
															StandName: ``,
															PartId: roll?.PartId,
															PartName: roll?.PartName,
														} as Stand;
													},
												);
											}
											setValue("PartName", `UR - ${partName ?? ""}`);
										}}
										disabled={modal === "delete" || modal === "update"}
										onForceAssembly={setForceAssembly}
										fieldError={error}
										required
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="DonutPartId"
								control={control}
								render={({ field }) => (
									<DonutSelector
										label="Donut"
										value={field.value}
										onChange={field.onChange}
										disabled={modal === "delete"}
										onForceAssembly={setForceAssembly}
										sectionFamilyIds={getValues("SectionFamilyIds") || null}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
							<Controller
								name="PartName"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										{...field}
										size="small"
										fullWidth
										error={!!error}
										helperText={error?.message}
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="RollStatusCode"
								control={control}
								rules={{ required: "Status is required" }}
								render={({ field, fieldState: { error } }) => (
									<RollStatusSelector
										required
										label="Status"
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										disabled={modal === "delete" || disabledStatusSelector}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="LocationId"
								control={control}
								render={({ field }) => (
									<LocationTreeSelector
										label="Location"
										value={field.value}
										onChange={field.onChange}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
							<Controller
								name="RFIDTag"
								control={control}
								render={({ field }) => (
									<TextField
										label="Tag"
										variant="outlined"
										{...field}
										size="small"
										fullWidth
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="RollPosition"
								control={control}
								rules={{ required: "Position is required" }}
								render={({ field, fieldState: { error } }) => (
									<RollPositionSelector
										required
										label="Position"
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="StandId"
								control={control}
								rules={{ required: "Stand is required" }}
								render={({ field, fieldState: { error } }) => (
									<LocationSelector
										label="Stand"
										value={field.value}
										onChange={field.onChange}
										onlyStands
										fieldError={error}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<SectionField
								label="Sections"
								onSelectionChanged={(selection: Section[]) => {
									const uniqueSectionFamilyIds = new Set<number>();

									selection.forEach((section) => {
										uniqueSectionFamilyIds.add(section.SectionFamilyId);
									});

									const sectionFamiliesIdsString = Array.from(
										uniqueSectionFamilyIds,
									).join(", ");

									setValue(
										"SectionFamilyIds",
										sectionFamiliesIdsString || null,
									);
									setSectionsSelected(selection);
								}}
								sectionsOfPart={sectionsSelected}
								partId={roll?.PartId!}
								disabled={modal === "delete"}
							/>
						</Grid2>
						{roll?.RollsetId ? <Grid2 size={{ md: 3, xs: 12 }} /> : null}
						{roll?.RollsetId ? (
							<Grid2 size={{ md: 6, xs: 12 }}>
								<RollsetCard
									sectionName={roll.RollsetSectionName!}
									standName={roll.RollsetStandName!}
									rollsetLetter={roll.RollsetLetter}
									topRollName={roll.RollsetTopRollName!}
									bottomRollName={roll.RollsetBottomRollName!}
									isActualTopRoll={roll.RollsetTopRollId === roll.PartId}
									status={roll.RollStatusCode!}
								/>
							</Grid2>
						) : null}
						<Grid2 size={{ sm: 12, md: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Updating Universal Roll"
			/>
			<RollsetDeactivationConfirmModal
				show={showConfirmDialog}
				entityName={roll?.PartName || ""}
				entityType="Roll"
				actionDescription={`Changing the ${getValues("RollStatusCode") === "K" ? "status to 'Broken'" : "Donut"} of this roll`}
				rollsetInfo={rollsetInfo}
				onConfirm={handleConfirmDeactivation}
				onCancel={handleCancelDeactivation}
			/>
		</>
	);
};
